import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface Log {
  id: string;
  admin_id: string;
  action: string;
  resource_type: string;
  resource_id: string;
  details: string;
  created_at: number;
}

interface LogsState {
  logs: Log[];
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
  filters: {
    action?: string;
    startDate?: number;
    endDate?: number;
  };
  lastFetchTime: number | null;
  cacheDuration: number;
  setLogs: (logs: Log[]) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setPagination: (pagination: Partial<LogsState['pagination']>) => void;
  setFilters: (filters: Partial<LogsState['filters']>) => void;
  isCacheValid: () => boolean;
  invalidateCache: () => void;
}

export const useLogsStore = create<LogsState>()(
  persist(
    (set, get) => ({
      logs: [],
      loading: false,
      error: null,
      pagination: {
        page: 1,
        pageSize: 20,
        total: 0,
        totalPages: 0,
      },
      filters: {},
      lastFetchTime: null,
      cacheDuration: 5 * 60 * 1000, // 5 minutes cache

      setLogs: (logs) => set({ logs, lastFetchTime: Date.now() }),

      setLoading: (loading) => set({ loading }),

      setError: (error) => set({ error }),

      setPagination: (pagination) =>
        set((state) => ({
          pagination: { ...state.pagination, ...pagination },
        })),

      setFilters: (filters) =>
        set((state) => ({
          filters: { ...state.filters, ...filters },
        })),

      isCacheValid: () => {
        const { lastFetchTime, cacheDuration, filters } = get();
        if (!lastFetchTime) return false;
        if (Date.now() - lastFetchTime >= cacheDuration) return false;
        if (filters.action || filters.startDate || filters.endDate) return false;
        return true;
      },

      invalidateCache: () => set({ logs: [], lastFetchTime: null }),
    }),
    {
      name: 'logs-storage',
      partialize: (state) => ({
        logs: state.logs,
        pagination: state.pagination,
        lastFetchTime: state.lastFetchTime,
      }),
    }
  )
);
